import React, { ChangeEvent, useEffect, useRef } from 'react';
import { useClickAway } from 'react-use';
import { Picker, BaseEmoji } from 'emoji-mart';
import cn from 'clsx';

import { CheckboxCircle, IconButton, Send, Smile } from '@bizzabo/design-system';

import { useStyles } from './index.style';

interface Message {
    text: string;
    anonymous: boolean;
}

export interface InputFooterProps {
    toggleInputExpand: (expand: boolean) => void;
    placeholderText?: string;
    message: Message;
    setMessage: React.Dispatch<React.SetStateAction<Message>>;
    isInputOpen?: boolean;
    enableAnonymousMessage?: boolean;
    isSendDisabled?: boolean;
    preventClickAwayElements?: string[];
    sendMessage: () => Promise<void>;
    displayReactionsButton?: boolean;
    isReactionsOpen?: boolean;
    toggleReactions?: (expand: boolean) => void;
    isEmojiButtonDisabled?: boolean;
    maxInputLength?: number;
    onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    fontSizeMobile?: number;
}

const preventClickAwayDefaultElements = ['send-icon', 'smile-icon'];
const DEFAULT_PLACEHOLDER_TEXT = 'Write a message';

const InputFooter: React.FC<InputFooterProps> = ({
    toggleInputExpand,
    placeholderText = DEFAULT_PLACEHOLDER_TEXT,
    message,
    setMessage,
    isInputOpen = false,
    enableAnonymousMessage = false,
    preventClickAwayElements = [],
    sendMessage,
    isSendDisabled = true,
    displayReactionsButton = false,
    isReactionsOpen = false,
    toggleReactions = () => {},
    isEmojiButtonDisabled = false,
    maxInputLength = 300,
    onChange = () => {},
    fontSizeMobile = 14,
}) => {
    const classes = useStyles({ fontSizeMobile });
    const mainRef = useRef<HTMLDivElement>(null);
    const inputAreaRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const noClickAwayElements = [...preventClickAwayDefaultElements, ...preventClickAwayElements];
    const textAreaPositionRef = useRef({ start: 0, end: 0 });

    useEffect(() => {
        const mainElement = mainRef.current;
        const adaptOpenedTextarea = (e: TransitionEvent) => {
            // verify the transition was on the footer content element and on the height property
            if (
                textareaRef.current &&
                (e.target as HTMLDivElement)?.className?.includes('footerContent') &&
                e.propertyName === 'min-height'
            ) {
                // overflow is set to auto only at transition end, so the scrollbar won't flicker when the textarea opens
                textareaRef.current.style.overflow = 'auto';
                // put the cursor at the end of the text when the textarea opens
                const textLength = textareaRef.current.value.length;
                textareaRef.current.setSelectionRange(textLength, textLength);
                updateCursorPosition();
            }
        };
        mainElement?.addEventListener('transitionend', adaptOpenedTextarea);

        return () => mainElement?.removeEventListener('transitionend', adaptOpenedTextarea);
    }, []);

    useClickAway(inputAreaRef, e => {
        const elementName = (e.target as HTMLTextAreaElement)
            .closest('[data-nocollapse]')
            ?.getAttribute('data-nocollapse');

        if (isInputOpen && !noClickAwayElements.includes(elementName as string)) {
            toggleInputExpand(false);
            toggleReactions(false);
        }
    });

    const updateCursorPosition = (startPos?: number, endPos?: number) => {
        textAreaPositionRef.current.start = startPos ?? textareaRef.current.selectionStart;
        textAreaPositionRef.current.end = endPos ?? textareaRef.current.selectionEnd;
    };

    const handleTextareaChange = (e, anonymous) => {
        onChange(e);
        updateCursorPosition();
        setMessage({ text: e.target.value, anonymous });
        toggleReactions(false);
    };

    const handleSendMessage = () => {
        if (message.text.trim().length > 0) {
            sendMessage();
        }
    };

    const handleKeyPress = e => {
        if (e.key == 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const renderTextarea = () => {
        const { text, anonymous } = message;
        const anonymousClass =
            enableAnonymousMessage || displayReactionsButton
                ? classes.inputOptionsWithAnonymous
                : classes.inputOptionsNoAnonymous;
        const isTextLengthExceeded = message.text.length >= maxInputLength;
        return (
            <React.Fragment>
                <textarea
                    autoFocus
                    ref={textareaRef}
                    maxLength={maxInputLength}
                    className={classes.inputText}
                    value={message.text}
                    placeholder={placeholderText}
                    onChange={e => handleTextareaChange(e, anonymous)}
                    onKeyPress={handleKeyPress}
                    onBlur={() => updateCursorPosition()}
                />
                <div className={`${classes.inputOptions} ${anonymousClass}`}>
                    {enableAnonymousMessage ? (
                        <div
                            className={`anonymous-section ${anonymous && 'active'}`}
                            onClick={() => setMessage({ text, anonymous: !anonymous })}
                        >
                            <CheckboxCircle checked={anonymous} className="anonymous-icon" />
                            <span className={`anonymous-text ${classes.inputOptionsText}`}>
                                Ask anonymously
                            </span>
                        </div>
                    ) : null}
                    {displayReactionsButton && (
                        <IconButton
                            className={cn(classes.smileIconButton, {
                                [classes.smileIconButtonOpen]: isReactionsOpen,
                            })}
                            icon={Smile}
                            disabled={isEmojiButtonDisabled}
                            onClick={() => toggleReactions(!isReactionsOpen)}
                            data-nocollapse="smile-icon"
                            role="emoji-button"
                        />
                    )}
                    <p
                        className={cn(classes.inputOptionsText, {
                            [classes.inputOptionsTextLimit]: isTextLengthExceeded,
                        })}
                    >
                        {`${message.text.length}/${maxInputLength}`}
                    </p>
                </div>
            </React.Fragment>
        );
    };

    const getClosedStateText = () => {
        const text = message.text;
        if (text.length === 0) {
            return placeholderText;
        } else if (text.length < 20) {
            return text;
        } else {
            const wordsArray = text.slice(0, 20).split(' ');
            if (wordsArray.length === 1) {
                return wordsArray[0];
            } else {
                return wordsArray.slice(0, -1).join(' ') + '...';
            }
        }
    };

    const handleEmojiPick = (emojiObj: BaseEmoji) => {
        const emoji = emojiObj.native;
        const { start, end } = textAreaPositionRef.current;
        const newText = `${message.text.slice(0, start)}${emoji}${message.text.slice(end)}`;

        // check emoji does not exceed max input length
        if (newText.length <= maxInputLength) {
            const newPos = textAreaPositionRef.current.start + emoji.length;
            updateCursorPosition(newPos, newPos);
            setMessage({ ...message, text: newText });
        }
    };

    return (
        <div
            className={cn(classes.footerContent, { [classes.expandedFooter]: isInputOpen })}
            ref={mainRef}
        >
            <div
                className={classes.inputWrapper}
                ref={inputAreaRef}
                role="input-wrapper"
                onClick={e => {
                    e.stopPropagation();
                    toggleInputExpand(true);
                }}
            >
                {isReactionsOpen && (
                    <span
                        className={classes.reactionsPickerSpanWrapper}
                        data-nocollapse="emoji-picker"
                        role="emoji-picker"
                    >
                        <Picker
                            showPreview={false}
                            showSkinTones={false}
                            onSelect={emoji => handleEmojiPick(emoji)}
                        />
                    </span>
                )}
                {isInputOpen ? (
                    renderTextarea()
                ) : (
                    <p className={classes.inputTextStyles}>{getClosedStateText()}</p>
                )}
            </div>
            <IconButton
                className={cn(classes.sendIconButton, 'send-icon-button')}
                icon={Send}
                onClick={handleSendMessage}
                disabled={isSendDisabled}
                data-nocollapse="send-icon"
                role="send-button"
            />
        </div>
    );
};

export default InputFooter;
